import { JSX, useContext, useEffect, useState } from 'react';
import AppContext from '../../AppContext';
import templates from '../../templates';
import { getFirstUnrestrictedVariationWert } from '../../utils/app-data';
import applyDefaults from '../../utils/defaults';
import { isAllDisabled, isAllHidden, isDisabled, isHidden } from '../../utils/restrictions';
import Modal from '../common/Modal';
import ModalOpenIcon from '../common/ModalOpenIcon';
import UnsafeHtml from '../common/UnsafeHtml';
import './Variation.scss';
import { getVariation } from '../../utils/artikel';

type VariationWertProps = {
  kEigenschaft: number;
  variationWert: ArtikelVariationWert;
  withoutPrice: boolean;
};

function VariationWert(props: VariationWertProps) {
  const { kEigenschaft, variationWert, withoutPrice } = props;
  const { appData, setDynamicAppData } = useContext(AppContext);

  const setVariationWert = () => {
    setDynamicAppData((dynamicAppData) => {
      const newDynamicAppData = { ...dynamicAppData };
      newDynamicAppData.selectedVariationWerte[kEigenschaft] = variationWert.kEigenschaftWert;
      return newDynamicAppData;
    });
  };
  const selected = appData.dynamic.selectedVariationWerte[kEigenschaft] === variationWert.kEigenschaftWert;
  const disabled = isDisabled(appData.static.artikel, appData.static.config, appData.dynamic.selectedVariationWerte, variationWert.kEigenschaftWert);
  const hidden = isHidden(appData.static.artikel, appData.static.config, appData.dynamic.selectedVariationWerte, variationWert.kEigenschaftWert);

  const [currentlyDisabled, setCurrentlyDisabled] = useState(disabled);
  const [currentlyHidden, setCurrentlyHidden] = useState(hidden);

  useEffect(() => {
    const recalculateDefaultValue = () => {
      let newSelectedVariationWerte = applyDefaults(appData, kEigenschaft);
      const aktuellerWert = newSelectedVariationWerte[kEigenschaft];
      const aktuellDisabled = isDisabled(appData.static.artikel, appData.static.config, newSelectedVariationWerte, aktuellerWert);
      const aktuellHidden = isHidden(appData.static.artikel, appData.static.config, newSelectedVariationWerte, aktuellerWert);
      const allDisabled = isAllDisabled(appData.static.artikel, appData.static.config, newSelectedVariationWerte, kEigenschaft);
      const allHidden = isAllHidden(appData.static.artikel, appData.static.config, newSelectedVariationWerte, kEigenschaft);
      if ((aktuellDisabled || aktuellHidden) && !allHidden && !allDisabled) {
        // Falls die Variation überhaupt keine wählbar Optionen hat und
        // falls der berechnete default Wert nicht möglich ist, wird der erste mögliche Wert ausgewählt
        const wert = getFirstUnrestrictedVariationWert(appData.static.artikel, appData.static.config, newSelectedVariationWerte, kEigenschaft);
        newSelectedVariationWerte = { ...appData.dynamic.selectedVariationWerte };
        newSelectedVariationWerte[kEigenschaft] = wert.kEigenschaftWert;
      }
      setDynamicAppData((dynamicAppData) => {
        const newDynamicAppData = { ...dynamicAppData };
        Object.keys(newSelectedVariationWerte)
          .forEach((key) => {
            newDynamicAppData.selectedVariationWerte[Number(key)] = newSelectedVariationWerte[Number(key)];
          });
        return newDynamicAppData;
      });
    };

    let somethingChanged = false;
    if (currentlyDisabled !== disabled) {
      setCurrentlyDisabled(disabled);
      somethingChanged = true;
    }
    if (currentlyHidden !== hidden) {
      setCurrentlyHidden(hidden);
      somethingChanged = true;
    }
    if (somethingChanged) {
      recalculateDefaultValue();
    }
  }, [appData, setDynamicAppData, kEigenschaft, currentlyDisabled, disabled, currentlyHidden, hidden]);

  return (
    <div className="variation-wert" style={{ display: hidden ? 'none' : 'block' }} data-keigenschaftwert={variationWert.kEigenschaftWert}>
      <div
        key={variationWert.kEigenschaftWert}
        className={`variation p-1 ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
        role="button"
        onClick={() => !disabled && setVariationWert()}
        onKeyDown={() => { }}
        tabIndex={0}
      >
        <img src={`https://www.diwaro.de/media/image/storage/variations/${variationWert.currentImagePath}`} alt={variationWert.cName} />
      </div>
      <div className={`${disabled ? 'disabled' : ''} p-1`}>
        <UnsafeHtml unsafeHtml={variationWert.cName} />
        {!withoutPrice
          && (
            <span className="aufpreis badge border-primary badge-light"><UnsafeHtml unsafeHtml={variationWert.cAufpreisLocalized[0]} />
              {variationWert.kEigenschaft !== appData.static.kEigenschaftVersand
                && appData.static.config.productType === 'rolladen'
                && Number(variationWert.fAufpreisNetto) > 0 ? ' pro m²' : ''}
              {variationWert.kEigenschaft !== appData.static.kEigenschaftVersand
                && (appData.static.config.productType === 'stahlwelle'
                  || appData.static.config.productType === 'lamelle'
                  || appData.static.config.productType === 'fuehrungsschiene')
                && Number(variationWert.fAufpreisNetto) > 0 ? ' pro m' : ''}
            </span>
          )}
      </div>
    </div>
  );
}

type VariationListeProps = {
  kEigenschaft: number;
  withoutPrice: boolean;
};

function VariationListe(props: VariationListeProps) {
  const { appData } = useContext(AppContext);
  const { kEigenschaft, withoutPrice } = props;

  let variationWerte: JSX.Element[];
  const werte = appData.static.artikel.variationen.find((variation) => variation.kEigenschaft === kEigenschaft)?.Werte;
  if (werte) {
    variationWerte = werte.map((wert) => (
      <VariationWert key={wert.kEigenschaftWert} kEigenschaft={kEigenschaft} variationWert={wert} withoutPrice={withoutPrice} />
    ));
  } else {
    variationWerte = [(
      <span key="not-found">Keine Werte in der Variation gefunden</span>
    )];
  }
  return (
    <div className="d-flex flex-row flex-wrap gap-5">
      {variationWerte}
    </div>
  );
}

type Props = {
  kEigenschaft: number;
  withoutPrice: boolean;
  infoModalContent?: React.ReactElement;
};

function Variation(props: Props) {
  const { appData } = useContext(AppContext);
  const { kEigenschaft, withoutPrice, infoModalContent } = props;
  const variation = getVariation(appData.static.artikel, kEigenschaft);
  const variationConfig = appData.static.config.variations[kEigenschaft];
  const name = variation.cName;
  const headerTemplateId = variationConfig?.headerTemplateId;
  const headerTemplateParameter = variationConfig?.headerTemplateParameter;
  const headerContent = templates(appData.static.artikel, headerTemplateParameter)[headerTemplateId];
  const infoModalTemplateId = variationConfig?.infoModalTemplateId;
  const infoModalTemplateParameter = variationConfig?.infoModalTemplateParameter;
  const infoModalContent2 = infoModalContent ?? templates(appData.static.artikel, infoModalTemplateParameter)[infoModalTemplateId];
  return (
    <div className="configurator__variation">
      <div className="d-flex flex-row justify-content-between align-items-center">
        <h3>{name}</h3>
        {!!infoModalContent
          && (
            <ModalOpenIcon modalId={`infoModal${kEigenschaft}`} />
          )}
      </div>
      <Modal
        id={`infoModal${kEigenschaft}`}
        title={name}
      >
        {infoModalContent2}
      </Modal>
      <hr />
      <div className="mt-3">
        {!!headerContent
          && (
            <div>{headerContent}</div>
          )}
        <VariationListe kEigenschaft={kEigenschaft} withoutPrice={withoutPrice} />
      </div>
    </div>
  );
}

Variation.defaultProps = { infoModalContent: null };

export default Variation;
